/**
 * An operator, as sections rather than one column of fifteen boxes.
 *
 * ONE screen shape for adding and for editing. The sections, the rail and
 * the field groups are the same on both, because fifteen boxes in a single
 * column is an unpleasant way to meet a record however you arrived at it.
 * What differs is what the sections are FOR, and that difference is spelt
 * out below rather than left to be inferred.
 *
 * The shape is dictated by what the 130 real operators look like, not by
 * what the form happens to hold:
 *
 *  - TWO fields are required — name and settlement currency. Everything else
 *    is optional, so the create must be reachable without walking a single
 *    screen further. The sections are sections, NOT gates: nothing later
 *    depends on anything earlier, so nothing is locked and any of them can
 *    be opened in any order.
 *  - 33 of 130 operators carry any billing at all. Billing is therefore a
 *    section somebody OPTS INTO when creating, named as optional on the way
 *    past, and never something to click through.
 *  - The five 0/1 flags have defaults nobody usually changes. They are shown
 *    with those defaults already chosen, and the review says what will be
 *    sent, so leaving them alone is an informed decision rather than a
 *    blind one.
 *
 * EDITING uses the same sections and differs in three ways, each because
 * the job is different:
 *
 *  - Nothing is numbered, nothing is "optional", and there is no Back or
 *    Next. An existing record is not being assembled in an order; somebody
 *    has come to change one field and every section is one click away.
 *  - Each section says on the rail how much of it is filled — `empty`, or
 *    `2 of 5`. Showing every box at once used to make "billing is blank"
 *    visible for free, and blank IS information, so the sections have to
 *    say it without being opened.
 *  - The last section is CHANGES, not Review: see `operatorChanges`.
 */
import { SETTLEMENT_CURRENCIES, operatorId } from './operatorGroups';
import {
  DEFAULT_OPERATOR_TYPE, OPERATOR_TYPE_LABELS, operatorTypeOf,
} from '../../Utils/operatorType';

/** The sections, in order. `optional` is shown to the user, not inferred. */
export const WIZARD_STEPS = [
  {
    key: 'company',
    title: 'The company',
    lead: 'Who they are, and what this record is for. '
      + 'Only the name and the settlement currency are needed.',
  },
  {
    key: 'billing',
    title: 'Billing',
    optional: true,
    lead: 'Only needed if this operator invoices us. Most do not — '
      + 'it can be filled in later, whenever the first invoice turns up.',
  },
  {
    key: 'settings',
    title: 'Settings',
    optional: true,
    lead: 'Already set the way operators normally run. Change them only if this one differs.',
  },
  {
    key: 'review',
    title: 'Review',
    lead: 'What will be created.',
  },
];

/**
 * The same four sections, worded for a record that already exists.
 *
 * No numbers and no "(optional)". Both are create's vocabulary: they answer
 * "how far through am I" and "may I skip this", and neither question is
 * being asked by somebody who opened an operator to fix its phone number.
 * What the rail says instead is how much each section HOLDS — see
 * `sectionFillLabel`.
 */
export const EDIT_STEPS = [
  {
    key: 'company',
    title: 'The company',
    lead: 'Who they are, and what this record is for.',
  },
  {
    key: 'billing',
    title: 'Billing',
    lead: 'What we invoice against. Empty is normal — most operators never invoice us.',
  },
  {
    key: 'settings',
    title: 'Settings',
    lead: 'How this operator is run day to day. Every one of these always has a value.',
  },
  {
    key: 'review',
    title: 'Changes',
    lead: 'What this edit will alter. Everything else is sent back exactly as it was.',
  },
];

export const stepsFor = (isNewOperator) => (isNewOperator ? WIZARD_STEPS : EDIT_STEPS);

export const stepIndexOf = (steps, key) => {
  const index = steps.findIndex((step) => step.key === key);
  return index === -1 ? 0 : index;
};

/** The billing boxes, in the order they are asked for. */
export const BILLING_FIELDS = [
  { key: 'name', label: 'Billing Name' },
  { key: 'address', label: 'Billing Address' },
  { key: 'city', label: 'Billing City' },
  { key: 'postcode', label: 'Billing Postcode' },
  { key: 'country', label: 'Billing Country' },
];

/**
 * The five 0/1 settings, with what each value is called on screen.
 *
 * `monthly_report_enabled` is the one default that differs between a new
 * operator and an existing one, and it differs on purpose — see
 * `defaultOperator`.
 */
export const OPERATOR_FLAGS = [
  {
    key: 'active',
    label: 'Active',
    options: [[1, 'Yes'], [0, 'No']],
  },
  {
    key: 'daily_digest_enabled',
    label: 'Daily Digest Email',
    options: [[1, 'Enabled'], [0, 'Disabled']],
    help: "Receive daily email digest of tomorrow's bookings at 5PM UTC",
  },
  {
    key: 'monthly_report_enabled',
    label: 'Monthly Report Email',
    options: [[1, 'Enabled'], [0, 'Disabled']],
    help: 'Receive monthly Excel report of completed transfers on the 1st of each month',
  },
  {
    key: 'hide_client_phone_number',
    label: 'Hide Client Phone Number',
    options: [[0, 'No'], [1, 'Yes']],
    help: "When enabled, the customer's phone number is hidden from "
      + 'operator-assignment and update emails sent to this operator',
  },
  {
    key: 'show_driver_fee',
    label: 'Show Driver the Fee',
    options: [[0, 'No'], [1, 'Yes']],
    help: "When enabled, this operator's drivers see what the operator "
      + 'is paid for a trip they are on, in the driver portal. Off by '
      + 'default: a driver portal otherwise shows no money at all, and '
      + "never the customer's price",
  },
];

/**
 * The company section's boxes, and how each reads when it is being reported
 * rather than typed into.
 *
 * One list, so the review, the changes list and the rail's "how full is
 * this" count can never disagree with the form about what the section
 * contains. `operator_type` reads as its label and never as a bare
 * `expense`, and it always has a value — a record carrying nothing at all
 * is an operator, which is the whole point of `operatorTypeOf`.
 */
export const COMPANY_SUMMARY = [
  { key: 'name', label: 'Name', read: (operator) => operator?.name },
  {
    key: 'operator_type',
    label: 'What this record is for',
    read: (operator) => OPERATOR_TYPE_LABELS[operatorTypeOf(operator)],
  },
  { key: 'settlement_currency', label: 'Settlement Currency', read: (operator) => operator?.settlement_currency },
  { key: 'email', label: 'Email address', read: (operator) => operator?.email },
  { key: 'phone_number', label: 'Phone Number', read: (operator) => operator?.phone_number },
  { key: 'website', label: 'Website', read: (operator) => operator?.website },
];

/** A `<select>`'s value as the reader of a summary should see it. */
export const flagText = (flag, value) => {
  const chosen = flag.options.find(([option]) => Number(option) === Number(value));

  return chosen ? chosen[1] : String(value ?? '');
};

/**
 * What an untouched operator carries.
 *
 * `billing: {}` is not decoration: the create endpoint reads
 * `data.billing.address` and a payload without the object takes the whole
 * request down with it. Sending the empty object means this screen is safe
 * against the API as deployed, whether or not the guard has shipped.
 */
export const defaultOperator = (isNewOperator) => ({
  // A supplier unless somebody says otherwise. This is the same default the
  // API and the Dynamo schema carry, and it is what makes an existing record
  // — which has no stored value at all — open on this form reading exactly as
  // it always did. See Utils/operatorType.
  operator_type: DEFAULT_OPERATOR_TYPE,
  active: 1,
  daily_digest_enabled: 0,
  monthly_report_enabled: isNewOperator ? 1 : 0, // Enabled by default for new operators only
  hide_client_phone_number: 0,
  show_driver_fee: 0,
  billing: {},
});

const filled = (value) => String(value ?? '').trim().length > 0;

/**
 * What is still missing before this operator can be created — in the words
 * the labels use, so the message names the box the user has to go and fill.
 */
export const missingRequired = (operator) => {
  const missing = [];

  if (!filled(operator?.name)) missing.push('Name');
  if (!SETTLEMENT_CURRENCIES.includes(String(operator?.settlement_currency || '').toUpperCase())) {
    missing.push('Settlement Currency');
  }

  return missing;
};

/** Both required fields answered, from whichever section the user is on. */
export const isCreatable = (operator) => missingRequired(operator).length === 0;

/** Whether anything at all was typed into the billing section. */
export const hasBillingDetails = (operator) => BILLING_FIELDS
  .some(({ key }) => filled(operator?.billing?.[key]));

/** Every value a section holds, in the order the section shows them. */
const SECTION_VALUES = {
  company: (operator) => COMPANY_SUMMARY.map(({ read }) => read(operator)),
  billing: (operator) => BILLING_FIELDS.map(({ key }) => operator?.billing?.[key]),
  settings: (operator) => OPERATOR_FLAGS.map(({ key }) => operator?.[key]),
};

/** How many of a section's boxes carry anything, out of how many there are. */
export const sectionFill = (operator, key) => {
  const read = SECTION_VALUES[key];

  if (!read) return null;

  const values = read(operator);

  return { filled: values.filter(filled).length, total: values.length };
};

/**
 * What the rail says a section holds, without it being opened.
 *
 * This exists because sections HIDE things and the flat form did not. On
 * that form, billing being blank was visible for free and it is real
 * information — an operator we have never invoiced against looks exactly
 * like one whose details nobody has got round to entering, and only the
 * boxes said which. A section nobody has opened must not read the same as
 * a section with nothing in it, so the rail answers the question up front:
 * `empty`, or `2 of 5`.
 *
 * Settings can never come back `empty` — all five flags always carry a
 * value — and saying `5 of 5` there is the honest answer rather than a
 * special case.
 */
export const sectionFillLabel = (operator, key) => {
  const count = sectionFill(operator, key);

  if (!count) return '';

  return count.filled === 0 ? 'empty' : `${count.filled} of ${count.total}`;
};

/**
 * What this edit will alter — every field whose value is not what it was
 * when the modal opened, as `was → will be`.
 *
 * This is what the last section is on the EDIT path, and it is a different
 * job from create's review. Create's review is the only place that says
 * what will be sent, because the record was assembled across four screens
 * and exists nowhere else. On edit the record already exists and the
 * sections say what it holds, so restating all fifteen boxes would only be
 * the form again with the boxes removed.
 *
 * The hazard on edit is the one the sections INTRODUCE: a field altered in
 * a section that is no longer on screen. The flat form could not have that
 * problem — every box was in view — and this is what replaces it. It is
 * also the only screen that can say "nothing has changed", which is the
 * answer somebody who opened the wrong operator needs.
 *
 * Compared as strings: a `<select>` hands back `'1'` where the record
 * carries `1`, and re-choosing the value that is already set is not a
 * change.
 */
export const operatorChanges = (before, after) => {
  const rows = [];

  const add = (label, from, to) => {
    if (String(from ?? '') !== String(to ?? '')) rows.push({ label, from, to });
  };

  COMPANY_SUMMARY.forEach(({ label, read }) => add(label, read(before), read(after)));

  BILLING_FIELDS.forEach(({ key, label }) => (
    add(label, before?.billing?.[key], after?.billing?.[key])
  ));

  OPERATOR_FLAGS.forEach((flag) => (
    add(flag.label, flagText(flag, before?.[flag.key]), flagText(flag, after?.[flag.key]))
  ));

  return rows;
};

/**
 * The record as the API insists on reading it.
 *
 * The five flags are validated as integers in 0..1 and a `<select>` hands
 * back strings, so each is coerced on the way out. Everything else is sent
 * as typed.
 */
export const operatorPayload = (operator) => ({
  ...operator,
  // Normalised rather than passed through: the API validates this against a
  // two-value enum and refuses anything else, and a record loaded from the
  // collection may carry nothing at all.
  operator_type: operatorTypeOf(operator),
  active: Number(operator.active),
  daily_digest_enabled: Number(operator.daily_digest_enabled),
  monthly_report_enabled: Number(operator.monthly_report_enabled),
  hide_client_phone_number: Number(operator.hide_client_phone_number),
  show_driver_fee: Number(operator.show_driver_fee),
});

/**
 * The record the create just made, found in a freshly fetched collection.
 *
 * The POST answers with an empty body — the operators Lambda throws the
 * controller's return value away — so the only way to lay hands on the new
 * record is to read the list back and recognise it by the name and currency
 * that were just sent.
 *
 * EXACTLY one match, or nothing. Two rows of the same name and currency
 * means the collection cannot say which one was just created, and the only
 * thing this answer is used for is pairing a second settlement currency to
 * it — pairing the wrong record would split a company's money between two
 * buckets nobody is reconciling. Nothing is offered rather than guessed.
 */
export const justCreated = (rows, operator) => {
  const matches = (rows || [])
    .filter((row) => operatorId(row))
    .filter((row) => row.name === operator?.name)
    .filter((row) => row.settlement_currency === operator?.settlement_currency);

  return matches.length === 1 ? matches[0] : null;
};
